import axios from 'axios';

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// In development, we'll use the proxy defined in setupProxy.js
// In production, we'll directly use the API URL from environment variables
const API_URL = isDevelopment ? '/api' : process.env.REACT_APP_API_URL;

// Validate that API_URL is set in production
if (!isDevelopment && !API_URL) {
  console.error('❌ REACT_APP_API_URL environment variable is not set for production!');
  throw new Error('REACT_APP_API_URL environment variable is required for production builds');
}

// For debugging
if (!isDevelopment) {
  console.log('API URL:', API_URL);
}

// Function to get the API URL for display purposes
export const getApiUrl = () => {
  // For display purposes, we want to show the actual URL, not the proxy path
  return isDevelopment ? process.env.REACT_APP_API_URL : API_URL;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Set withCredentials to false to avoid CORS preflight issues
  withCredentials: false,
  // Add timeout configuration to handle slow responses
  timeout: 60000, // 60 seconds timeout
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Log only critical API requests in development mode
    if (isDevelopment && config.url && config.url.includes('/auth')) {
      console.log(`API Request: ${config.method?.toUpperCase() || 'GET'} ${config.url}`);
    }

    const token = sessionStorage.getItem('token');
    if (token) {
      // Ensure headers object exists
      config.headers = config.headers || {};

      // Set Authorization header with Bearer token
      config.headers.Authorization = `Bearer ${token}`;

      // Debug logging for authorization header issues
      if (isDevelopment && config.url && (config.url.includes('/presigned-urls') || config.url.includes('/records'))) {
        console.log('🔐 Authorization header set:', {
          url: config.url,
          tokenLength: token.length,
          tokenStart: token.substring(0, 10) + '...',
          fullAuthHeader: config.headers.Authorization.substring(0, 20) + '...'
        });


      }

      // Auth token added silently
    } else if (isDevelopment && config.url && config.url.includes('/upload/')) {
      console.warn('No token found for upload request!');
    }

    // Handle FormData requests - ensure proper Content-Type
    if (config.data && config.data instanceof FormData) {
      // For FormData, we need to remove Content-Type completely to let the browser set it with boundary
      // This is crucial for multipart/form-data to work correctly
      delete config.headers['Content-Type'];

      if (isDevelopment && process.env.NODE_ENV === 'development') {
        console.log('Detected FormData request - removed Content-Type to allow browser to set multipart boundary');
        console.log('Upload request headers (excluding token):', {
          'Content-Type': config.headers['Content-Type'],
          'Accept': config.headers['Accept'],
          'X-Requested-With': config.headers['X-Requested-With']
        });
        console.log('Upload request has Authorization header:', !!config.headers.Authorization);
      }
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Log API responses in development mode (reduced verbosity)
    if (isDevelopment && response.status >= 400) {
      console.log(`API Response: ${response.status} ${response.config.method?.toUpperCase() || 'GET'} ${response.config.url}`);
    }

    // Check if response follows consistent API format
    const data = response.data;
    if (data && typeof data === 'object' && typeof data.success === 'boolean') {
      // Handle consistent API error responses
      if (!data.success && data.error) {
        if (isDevelopment) {
          console.log('API returned consistent error response:', data);
        }

        // Create a custom error that preserves the consistent format
        const error = new Error(data.message || 'API request failed');
        error.response = {
          ...response,
          data: data
        };
        error.isConsistentApiError = true;
        return Promise.reject(error);
      }
    }

    return response;
  },
  (error) => {
    // Log errors in development mode
    if (isDevelopment) {
      console.error('API Response Error:', error);
      if (error.response) {
        console.error(`Status: ${error.response.status}`, error.response.data);
      }
    }

    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Unauthorized access detected for:', error.config?.url);
        console.warn('Error details:', error.response.data);
      }

      // Don't immediately redirect for certain endpoints - let the component handle the error
      const isUploadEndpoint = error.config?.url?.includes('/upload/');
      const isDocumentationEndpoint = error.config?.url?.includes('/docs/');

      if (!isUploadEndpoint && !isDocumentationEndpoint) {
        // For core API endpoints, redirect immediately (user is not authenticated)
        sessionStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        // For upload and documentation endpoints, just log the error and let the component handle it
        if (isUploadEndpoint) {
          console.warn('Upload endpoint returned 401 - check permissions for this channel');
        }
        if (isDocumentationEndpoint) {
          console.warn('Documentation endpoint returned 401 - user may not have documentation access');
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
